import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Box, Chip, IconButton } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

const CategoryTabs = ({ categories, selectedCategories, handleCategoryChange }) => {
    const scrollRef = useRef(null);
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);

    // Check if arrows are needed
    const checkScroll = () => {
        const el = scrollRef.current;
        if (el) {
            const hasHorizontalScroll = el.scrollWidth > el.clientWidth;
            setShowLeft(el.scrollLeft > 0);
            setShowRight(hasHorizontalScroll && el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
        }
    };

    useEffect(() => {
        // Check scroll after component mounts and categories are loaded
        const timer = setTimeout(() => {
            checkScroll();
        }, 100);

        window.addEventListener('resize', checkScroll);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', checkScroll);
        };
    }, [categories]); // Re-check when categories change

    // Additional effect to check scroll when content changes
    useEffect(() => {
        if (categories.length > 0) {
            const timer = setTimeout(() => {
                checkScroll();
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [categories.length]);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = 200; // adjust scroll step
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
            // Re-check scroll state after scrolling
            setTimeout(checkScroll, 300);
        }
    };

    return (
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', mb: 1 }}>
            {/* Left Arrow */}
            {showLeft && (
                <IconButton
                    onClick={() => scroll('left')}
                    sx={{
                        position: 'absolute',
                        left: -8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        background: '#006CDD',
                        boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.25)',
                        width: 32,
                        height: 32,
                        '&:hover': {
                            background: '#0056b3',
                            transform: 'translateY(-50%) scale(1.05)',
                        },
                    }}
                >
                    <ArrowBack sx={{ color: '#fff', fontSize: 18 }} />
                </IconButton>
            )}

            {/* Scrollable container */}
            <Box
                ref={scrollRef}
                onScroll={checkScroll}
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    scrollbarWidth: 'none', // hide scrollbar in Firefox
                    '&::-webkit-scrollbar': { display: 'none' }, // hide scrollbar in Chrome
                    gap: '6px',
                    px: showLeft ? 6 : 0, // padding for arrows
                    width: '100%',
                    minHeight: '48px', // Ensure minimum height for proper arrow positioning
                }}
            >
                {categories.map((category, index) => {
                    const isSelected = category._id === 'all' ? selectedCategories.length === 0 : selectedCategories.includes(category._id);
                    return (
                        <Chip
                            key={category._id}
                            label={category.name}
                            onClick={() => handleCategoryChange(category)}
                            sx={{
                                padding: '8px 16px',
                                height: '40px',
                                fontSize: '16px',
                                fontWeight: 400,
                                borderRadius: index === 0 ? '40px' : '100px',
                                ...(isSelected
                                    ? {
                                          backgroundColor: '#007AFF',
                                          color: 'rgba(255, 255, 255, 0.87)',
                                          border: '1px solid #0066E1',
                                          boxShadow: '0 2px 2px 0 rgba(0, 122, 255, 0.25)',
                                          '&:hover': {
                                              backgroundColor: '#007AFF',
                                              boxShadow: '0 2px 2px 0 rgba(0, 122, 255, 0.25)',
                                          },
                                      }
                                    : {
                                          backgroundColor: '#fff',
                                          color: 'rgba(0, 0, 0, 0.87)',
                                          border: '1px solid #DCDCDC',
                                          boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                          '&:hover': {
                                              backgroundColor: '#fff',
                                              boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                          },
                                      }),
                            }}
                        />
                    );
                })}
            </Box>

            {/* Right Arrow */}
            {showRight && (
                <IconButton
                    onClick={() => scroll('right')}
                    sx={{
                        position: 'absolute',
                        right: -8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        background: '#006CDD',
                        boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.25)',
                        width: 32,
                        height: 32,
                        '&:hover': {
                            background: '#0056b3',
                            transform: 'translateY(-50%) scale(1.05)',
                        },
                    }}
                >
                    <ArrowForward sx={{ color: '#fff', fontSize: 18 }} />
                </IconButton>
            )}
        </Box>
    );
};

export default CategoryTabs;
